import type { AuthConfig } from '@micronet/auth';

// Runtime config, one object for the app and every product it loads: the deploy
// writes /config.js (window.__APP_CONFIG__) before the bundle, and each product's
// own config/env.ts reads the same keys. Vite env and localhost are the local-dev
// fallbacks, as in the products.
export type RuntimeConfig = Partial<{
  /** The banner's name. */
  appName: string;
  /** The products this deployment runs, by license name; a list or "a,b". */
  products: string[] | string;
  coreApi: string;
  storagehubApi: string;
  conciliatronApi: string;
  stocksApi: string;
  confighubApi: string;
  factoryApi: string;
  workspaceId: number | string;
  authzUserId: string;
  azureClientId: string;
  azureTenantId: string;
  azureApiScope: string;
  devAuthBypass: boolean;
}>;

const rt: RuntimeConfig =
  (typeof window !== 'undefined' &&
    (window as unknown as { __APP_CONFIG__?: RuntimeConfig }).__APP_CONFIG__) ||
  {};

const devDefault = (value: string): string => (import.meta.env.DEV ? value : '');

export function parseProducts(raw: string[] | string | undefined): string[] {
  const list = Array.isArray(raw) ? raw : (raw ?? '').split(',');
  return list.map((p) => p.trim()).filter(Boolean);
}

const rawWorkspaceId = rt.workspaceId ?? import.meta.env.VITE_WORKSPACE_ID;

export const ENV = {
  appName: rt.appName ?? import.meta.env.VITE_APP_NAME ?? 'MicroNet',
  products: parseProducts(
    rt.products ?? import.meta.env.VITE_PRODUCTS ?? devDefault('conciliatron,stocks,factory')
  ),
  coreApi:
    rt.coreApi ?? import.meta.env.VITE_CORE_API ?? devDefault('http://localhost:8081/api/v1'),
  storagehubApi:
    rt.storagehubApi ??
    import.meta.env.VITE_STORAGEHUB_API ??
    devDefault('http://localhost:8082/api/v1'),
  conciliatronApi:
    rt.conciliatronApi ??
    import.meta.env.VITE_CONCILIATRON_API ??
    devDefault('http://localhost:8083'),
  stocksApi: rt.stocksApi ?? import.meta.env.VITE_STOCKS_API ?? devDefault('http://localhost:8085'),
  confighubApi:
    rt.confighubApi ?? import.meta.env.VITE_CONFIGHUB_API ?? devDefault('http://localhost:8087'),
  factoryApi:
    rt.factoryApi ?? import.meta.env.VITE_FACTORY_API ?? devDefault('http://localhost:8088/api/v1'),
  workspaceId:
    rawWorkspaceId === undefined || rawWorkspaceId === '' ? undefined : Number(rawWorkspaceId),
  authzUserId: rt.authzUserId ?? import.meta.env.VITE_AUTHZ_USER_ID ?? '1',
} as const;

type Base =
  | 'coreApi'
  | 'storagehubApi'
  | 'conciliatronApi'
  | 'stocksApi'
  | 'confighubApi'
  | 'factoryApi';

// What each product's own config/env.ts refuses to load without.
const PRODUCT_BASES: Record<string, Base[]> = {
  conciliatron: ['conciliatronApi'],
  stocks: ['stocksApi', 'confighubApi'],
  factory: ['factoryApi'],
};

/** The API bases the app and the deployment's products need but lack. */
export function missingBases(env: Pick<typeof ENV, Base | 'products'>): Base[] {
  const needed: Base[] = [
    'coreApi',
    'storagehubApi',
    ...env.products.flatMap((p) => PRODUCT_BASES[p] ?? []),
  ];
  return needed.filter((key) => !env[key]);
}

// Fail at load, as the products do: an app with no API to call can only show
// errors later, one request at a time.
const missing = missingBases(ENV);
if (missing.length > 0) {
  throw new Error(
    `Missing API base URL(s): ${missing.join(', ')}. The deploy must serve a ` +
      '/config.js that sets window.__APP_CONFIG__ with every base before the bundle loads.'
  );
}

export const AUTH: AuthConfig = {
  clientId: rt.azureClientId ?? (import.meta.env.VITE_AZURE_CLIENT_ID as string | undefined),
  tenantId: rt.azureTenantId ?? (import.meta.env.VITE_AZURE_TENANT_ID as string | undefined),
  apiScope: rt.azureApiScope ?? (import.meta.env.VITE_AZURE_API_SCOPE as string | undefined),
  devBypass: rt.devAuthBypass ?? import.meta.env.VITE_DEV_AUTH_BYPASS === 'true',
};
